import { useEffect, type ReactNode } from "react";
import { useSelector } from "@tanstack/react-store";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { useAuthConfigQuery } from "../../hooks/useAuthConfigQuery";
import { localStorageConfigStore } from "../../stores/localStorageConfigStore";
import { MockAuth0Provider, mockAuth0User } from "../../mocks/auth0";
import { configureAuth } from "../../utils/apiClient";
import { setErrorReporterAuth } from "../../utils/errorReporter";
import { reportClientError } from "../../utils/reportClientError";
import { AIRLINE_CODE_CLAIM, ROLE_CLAIM } from "../../constants/auth";
import { LoadingSpinner } from "../LoadingSpinner";
import { ErrorComponent } from "../../routes/error";

const AuthConfigurator = ({ children }: { children: ReactNode }) => {
  const { getAccessTokenSilently, error } = useAuth0();

  useEffect(() => {
    configureAuth(getAccessTokenSilently);
    setErrorReporterAuth(true);
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (error) {
      reportClientError(error, "AUTH_CALLBACK_FAILED");
    }
  }, [error]);

  return <>{children}</>;
};

export const AuthWrapper = ({ children }: { children: ReactNode }) => {
  const psmConfigOverrideUserRole = useSelector(
    localStorageConfigStore,
    ({ psmConfigOverrideUserRole }) => psmConfigOverrideUserRole
  );
  const { data: authConfig, error, isLoading } = useAuthConfigQuery();

  if (error) return <ErrorComponent />;
  if (isLoading) return <LoadingSpinner />;

  const mockAuthEnabled = import.meta.env.VITE_MOCK_AUTH === 'true';
  const effectiveRole = psmConfigOverrideUserRole || (mockAuthEnabled ? 'internal_admin' : null);

  if (effectiveRole) {
    const user = mockAuth0User({
      name: `Dev User (${effectiveRole})`,
      [ROLE_CLAIM]: effectiveRole,
      [AIRLINE_CODE_CLAIM]: "XC",
    });

    return (
      <MockAuth0Provider contextOverrides={{ user }}>
        <AuthConfigurator>{children}</AuthConfigurator>
      </MockAuth0Provider>
    );
  }

  return (
    <Auth0Provider
      domain={authConfig!.domain}
      clientId={authConfig!.clientId}
      onRedirectCallback={(appState) => {
        // After Auth0 login, navigate to the page the user originally requested
        // (stored in appState.returnTo by requireAuth) instead of defaulting to "/"
        window.history.replaceState({}, "", appState?.returnTo || "/");
      }}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: authConfig!.audience,
        scope: "openid profile email read:metrics write:users read:allairlines write:airlines read:airlinegroups write:airlinegroups deactivate:airlines",
      }}
    >
      <AuthConfigurator>{children}</AuthConfigurator>
    </Auth0Provider>
  );
};
